import { CommonModule, isPlatformBrowser, registerLocaleData } from '@angular/common';
import localeDECH from '@angular/common/locales/de-CH';
import localeFRCH from '@angular/common/locales/fr-CH';
import localeITCH from '@angular/common/locales/it-CH';
import {
  Component,
  DOCUMENT,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  REQUEST,
  afterNextRender,
  inject,
  injectAsync,
  signal,
} from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { AdminComponent } from '@app/components/admin/admin.component';
import { FooterComponent } from '@app/components/footer/footer.component';
import { ProtektaFooterComponent } from '@app/components/protekta/footer/footer.component';
import { HeaderComponent } from '@app/components/header/header.component';
import { ProtektaHeaderComponent } from '@app/components/protekta/header/header.component';
import { SitewideAlertsComponent } from '@app/components/sitewide-alerts/sitewide-alerts.component';
import { ResolverComponent } from '@app/components/resolver/resolver.component';
import { MetatagService } from '@app/services/metatag.service';
import { PlatformContextService } from '@app/services/platform-context.service';
import { FastlyChallengeService } from '@app/services/fastly-challenge.service';
import { AuthService } from '@app/services/auth.service';
import { DrupalService } from '@app/services/drupal.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { TextsPipe } from './pipes/texts.pipe';
import { BetterRouterLinkDirective } from './directives/better-router-link.directive';
import { take, filter } from 'rxjs';

@Component({
  selector: 'mobi-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    RouterOutlet,
    AdminComponent,
    FooterComponent,
    ProtektaFooterComponent,
    HeaderComponent,
    ProtektaHeaderComponent,
    SitewideAlertsComponent,
    ResolverComponent,
    CommonModule,
    TextsPipe,
    BetterRouterLinkDirective,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  hideHeader = signal(false);
  hideFooter = signal(false);
  hideBreadcrumb = signal(false);
  interactive = signal(false);
  navigating = signal(false);

  #request = inject(REQUEST, { optional: true });
  #platformId = inject(PLATFORM_ID);
  #drupal = inject(DrupalService);
  #meta = inject(MetatagService);
  #device = inject(DeviceDetectorService);
  #router = inject(Router);
  #doc = inject(DOCUMENT);
  #auth = inject(AuthService);

  platformService = inject(PlatformContextService);

  #getTealium = injectAsync(() =>
    import('@app/services/utag.service').then((m) => m.TealiumUtagService)
  );
  #challenge = inject(FastlyChallengeService);
  #animationInitCls = 'mobi-ui-animation-appear-up-init';
  #animationTriggerCls = 'mobi-ui-animation-appear-up-active';

  #observedElements = new Set<Element>();
  #interactionHandler: () => void;
  #animationObserver: IntersectionObserver | null;
  #animationMutationObserver: MutationObserver | null;
  #observeQueued: boolean;
  #tealiumLoadHandler: (() => void) | null;
  #tealiumLoadTimeoutId: ReturnType<typeof setTimeout> | null;
  #autoLoginAttempted: boolean;

  constructor() {
    registerLocaleData(localeDECH);
    registerLocaleData(localeFRCH);
    registerLocaleData(localeITCH);

    this.#interactionHandler = () => this.onInteract();
    this.#animationObserver = null;
    this.#animationMutationObserver = null;
    this.#observeQueued = false;
    this.#tealiumLoadHandler = null;
    this.#tealiumLoadTimeoutId = null;
    this.#autoLoginAttempted = false;
    this.#syncQueryState(false);

    if (!isPlatformBrowser(this.#platformId)) {
      this.#device.setDeviceInfo(this.#request?.headers.get('user-agent') || '');
      // https://github.com/angular/universal/issues/1826#issuecomment-963437238
    }

    afterNextRender({
      write: () => {
        this.#setPlatformFavicons();

        // Bot protection: on the Fastly challenge landing page, return the user to ?destination=.
        // call it in afterNextRender as it is only needed on the first client render
        void this.#challenge.handleReturn();

        // Prevent any sort of animations on the initial page load (SSR response)
        // this prevents the animation from playing on the first page load
        const initialPath = this.#doc.location.pathname;
        this.#router.events
          .pipe(
            filter((e): e is NavigationStart => e instanceof NavigationStart),
            filter((e) => (e.url.split(/[?#]/, 1)[0] || '/') !== initialPath),
            take(1)
          )
          .subscribe(() => {
            if (this.interactive()) return;
            this.interactive.set(true);
            this.observeViewEnterElements();

            if (!this.#animationMutationObserver) {
              this.#animationMutationObserver = new MutationObserver(() => {
                if (this.#observeQueued) return;
                this.#observeQueued = true;
                requestAnimationFrame(() => {
                  this.#observeQueued = false;
                  this.observeViewEnterElements();
                });
              });
            }

            this.#animationMutationObserver.observe(this.#doc.body, {
              childList: true,
              subtree: true,
            });
          });

        this.#doc.addEventListener('pointerdown', this.#interactionHandler, {
          passive: true,
          once: true,
        });
        this.#doc.addEventListener('touchstart', this.#interactionHandler, {
          passive: true,
          once: true,
        });
        this.#doc.addEventListener('keydown', this.#interactionHandler, {
          once: true,
        });
        this.#doc.addEventListener('submit', this.#interactionHandler, {
          once: true,
        });

        // Prevent footer flashing when navigating
        this.#router.events.subscribe((event) => {
          if (event instanceof NavigationStart) this.navigating.set(true);
          if (event instanceof NavigationEnd) {
            this.navigating.set(false);
            this.#drupal.setQueryParams(event.urlAfterRedirects);
            this.#syncQueryState();
            if (isPlatformBrowser(this.#platformId)) {
              void this.#auth.refreshAuthenticationState();
            }
          }
        });

        this.#scheduleTealiumInitialization();
      },
    });
  }

  #setPlatformFavicons() {
    const faviconBasePath = this.platformService.isProtekta()
      ? '/favicon/protekta'
      : '/favicon';

    this.#upsertHeadLink({
      rel: 'icon',
      href: `${faviconBasePath}/favicon-96x96.png`,
      type: 'image/png',
      sizes: '96x96',
    });
    this.#upsertHeadLink({
      rel: 'icon',
      href: `${faviconBasePath}/favicon.svg`,
      type: 'image/svg+xml',
    });
    this.#upsertHeadLink({
      rel: 'shortcut icon',
      href: `${faviconBasePath}/favicon.ico`,
    });
    this.#upsertHeadLink({
      rel: 'apple-touch-icon',
      href: `${faviconBasePath}/apple-touch-icon.png`,
      sizes: '180x180',
    });
    this.#upsertHeadLink({
      rel: 'manifest',
      href: `${faviconBasePath}/site.webmanifest`,
    });
  }

  #upsertHeadLink(input: { rel: string; href: string; type?: string; sizes?: string }) {
    const selector = [
      `head > link[rel="${input.rel}"]`,
      input.type ? `[type="${input.type}"]` : '',
      input.sizes ? `[sizes="${input.sizes}"]` : '',
    ].join('');
    const existing = this.#doc.querySelector(selector);
    const link = existing || this.#doc.createElement('link');

    link.setAttribute('rel', input.rel);
    link.setAttribute('href', input.href);

    if (input.type) {
      link.setAttribute('type', input.type);
    } else {
      link.removeAttribute('type');
    }

    if (input.sizes) {
      link.setAttribute('sizes', input.sizes);
    } else {
      link.removeAttribute('sizes');
    }

    if (!existing) {
      this.#doc.head.appendChild(link);
    }
  }

  #scheduleTealiumInitialization() {
    const view = this.#doc.defaultView;
    if (!view) return;

    const scheduleInitialization = () => {
      this.#tealiumLoadTimeoutId = setTimeout(() => {
        this.#tealiumLoadTimeoutId = null;
        void this.onInteract();
      }, 5000);
    };

    if (this.#doc.readyState === 'complete') {
      scheduleInitialization();
      return;
    }

    this.#tealiumLoadHandler = scheduleInitialization;
    view.addEventListener('load', this.#tealiumLoadHandler, { once: true });
  }

  #clearTealiumInitializationScheduling() {
    if (this.#tealiumLoadHandler && this.#doc.defaultView) {
      this.#doc.defaultView.removeEventListener('load', this.#tealiumLoadHandler);
      this.#tealiumLoadHandler = null;
    }

    if (this.#tealiumLoadTimeoutId) {
      clearTimeout(this.#tealiumLoadTimeoutId);
      this.#tealiumLoadTimeoutId = null;
    }
  }

  #removeTealiumInteractionListeners() {
    this.#doc.removeEventListener('pointerdown', this.#interactionHandler);
    this.#doc.removeEventListener('touchstart', this.#interactionHandler);
    this.#doc.removeEventListener('keydown', this.#interactionHandler);
    this.#doc.removeEventListener('submit', this.#interactionHandler);
  }

  async onInteract() {
    (await this.#getTealium()).initialize();
    this.#clearTealiumInitializationScheduling();
    this.#removeTealiumInteractionListeners();
  }

  observeViewEnterElements() {
    if (!this.#animationObserver) {
      this.#animationObserver = new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(this.#animationTriggerCls);

            obs.unobserve(entry.target);
            this.#observedElements.delete(entry.target);
          }
        });
      });
    }

    const animationElements = this.#doc.querySelectorAll('.view-enter-animation');
    animationElements.forEach((elem) => {
      if (!this.#observedElements.has(elem)) {
        if (!elem.classList.contains(this.#animationInitCls)) {
          elem.classList.add(this.#animationInitCls);
        }
        this.#animationObserver!.observe(elem);
        this.#observedElements.add(elem);
      }
    });
  }

  ngOnInit() {
    this.#meta.initialize();
    if (isPlatformBrowser(this.#platformId)) {
      void this.#auth.initialize();
    }
    this.#syncQueryState();
  }
  #syncQueryState(authenticate = true): void {
    const params = this.#drupal.queryParams();
    this.hideHeader.set(params.get('hide-header') === '1');
    this.hideFooter.set(params.get('hide-footer') === '1');
    this.hideBreadcrumb.set(params.get('hide-breadcrumb') === '1');

    if (
      authenticate &&
      isPlatformBrowser(this.#platformId) &&
      ['true', '1'].includes(params.get('auth') ?? '') &&
      !this.#autoLoginAttempted
    ) {
      this.#autoLoginAttempted = true;
      void this.#auth.ensureAuthenticated();
    }
  }

  ngOnDestroy() {
    this.#clearTealiumInitializationScheduling();
    this.#removeTealiumInteractionListeners();

    this.#animationMutationObserver?.disconnect();
    this.#animationObserver?.disconnect();
  }
}
